import React from "react";
import {Col, Form, Label, Modal, ModalBody, ModalHeader, Row} from "reactstrap";
import LabelInput from "../../../components/Common/LabelInput";
import {useDispatch, useSelector} from "react-redux";
import * as yup from "yup";
import {useFormik} from "formik";
import {addCrmSystem, clearCrmSystem, modalCrmSystem, updateCrmSystem} from "../../../store/crmSystems/actions";
import {ICrmSystem, ICrmSystemForm} from "../../../store/crmSystems/types";
import Select from "../../../components/Common/Select";
import usePermissions from "../../../hooks/usePermissions";
import useOffices from "../../../hooks/useOffices";

interface ModalOfficeProps {
    isOpen: boolean;
}

const ModalCrmSystems: React.FC<ModalOfficeProps> = ({isOpen}) => {
    const dispatch = useDispatch();
    const {hasPermission} = usePermissions();

    const item = useSelector((state: any) => (state.CrmSystem.item) as ICrmSystem | null);

    const {user_offices, user_office_ids} = useOffices()
    const defaultOffice = user_office_ids.length === 1 ? user_office_ids[0] : null;
    const officeSelect =
        user_offices.map((item: any) => ({
            value: item.id,
            label: item.name,
        }))

    const validationSchema: yup.Schema<ICrmSystemForm> = yup.object().shape({
        name: yup.string()
            .min(1, "min 1 character")
            .max(75, "max 75 characters")
            .required("required"),

        office_id: yup.number()
            .min(0, "required")
            .required("required"),
    });

    const initialValues: ICrmSystemForm = {
        name: item?.name || "",
        office_id: item?.office_id || defaultOffice
    }

    const handleSubmitForm = () => {
        if (!item) dispatch(addCrmSystem({name: values.name, office_id: values.office_id}))
        else dispatch(updateCrmSystem({name: values.name, office_id: values.office_id}, item.id))
    };

    const {
        values,
        handleChange,
        submitForm,
        handleBlur,
        setFieldValue,
        errors,
        touched,
        isValid,
        resetForm,
    } = useFormik({
        initialValues: initialValues,
        onSubmit: handleSubmitForm,
        validationSchema: validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true
    });

    const close = () => {
        dispatch(modalCrmSystem(false));
        dispatch(clearCrmSystem());
        resetForm();
    };

    const handleChangeSelect = (name: string, value: any) => {
        setFieldValue(name, value.value)
    }

    const shouldSelectOffice = hasPermission("create-crm-system-full") || hasPermission("update-crm-system-full")

    return (
        <Modal isOpen={isOpen} toggle={close}>
            <ModalHeader toggle={close} tag="h4">
                {item ? "Edit" : "Add"} CRM System
            </ModalHeader>
            <ModalBody>
                <Form className="needs-validation">
                    <Row>
                        <Col xs={12}>
                            <div className="mb-3">
                                <LabelInput
                                    label="Name*"
                                    placeholder="Name*"
                                    name="name"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.name || ""}
                                    hasError={!!(errors.name && touched.name)}
                                    errorText={errors.name}
                                />
                            </div>
                            {shouldSelectOffice && <div className='mb-3'>
                                <Label>Office: </Label>
                                <Select
                                    isSearchable
                                    name="office_id"
                                    label="Office"
                                    placeholder="Office"
                                    options={officeSelect}
                                    onChange={(value: any) => handleChangeSelect("office_id", value)}
                                    value={officeSelect.find((item: any) => item.value === values.office_id)}
                                />
                            </div>}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <div className="text-end">
                                <button
                                    type="button"
                                    className="btn btn-success save-user"
                                    onClick={submitForm}
                                    disabled={!isValid}
                                > Save
                                </button>
                            </div>
                        </Col>
                    </Row>
                </Form>
            </ModalBody>
        </Modal>
    )
}

export default ModalCrmSystems;